import { I18n } from '@calendly/i18n';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { useState, useRef, useEffect, useMemo, useCallback, forwardRef } from 'react';
import { GrowableSearchSection, ThinkingLoader, AiResponseMessage, COMPOSER_LOCATION, QUERY_SOURCE_TYPE, isUserMessage } from 'components/agent_hub_components';
import defaultStyles from './ChatPage.module.css';
import embeddedStyles from './EmbeddedChatPage.module.css';
const userMessageContentCss = "u3jemy1";
const UserMessageContent = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(userMessageContentCss, props.className)} />;
});
export const ChatPage = ({
  messages = [],
  onSendMessage,
  isLoading = false,
  onCancelGeneration,
  analyticsHandlers = {},
  onNewChatPressed,
  toolCalls = [],
  embedded = false
}) => {
  const styles = embedded ? embeddedStyles : defaultStyles;
  const [currentMessage, setCurrentMessage] = useState('');
  const [inputHeight, setInputHeight] = useState(80); // Default height estimate
  const inputContainerRef = useRef(null);
  const lastUserMessageRef = useRef(null);
  const secondLastUserMessageRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const chatContainerRef = useRef(null);
  const growableSearchRef = useRef(null);
  const minMessagesContainerHeight = useRef(0);
  const [readyForAutoScroll, setReadyForAutoScroll] = useState(false);

  // Extract the complex expression to a memoized value
  const userMessageIds = useMemo(() => messages.filter(isUserMessage).map(msg => msg.id).join(','), [messages]);
  const sendMessage = (message, querySourceType) => {
    if (!message || !message.trim()) return;
    setCurrentMessage('');
    onSendMessage(message, querySourceType);
  };
  const handleSearch = message => {
    sendMessage(message, QUERY_SOURCE_TYPE.TEXT);
  };
  const handleSuggestionClick = suggestion => {
    sendMessage(suggestion, QUERY_SOURCE_TYPE.SUGGESTION);
  };
  const focusComposer = useCallback(() => {
    if (growableSearchRef.current) {
      growableSearchRef.current.focus();
    }
  }, []);

  // Update input height when the component mounts or when the input container changes
  useEffect(() => {
    const updateInputHeight = () => {
      if (inputContainerRef.current) {
        const height = inputContainerRef.current.offsetHeight;
        setInputHeight(height);
      }
    };

    // Initial measurement
    updateInputHeight();

    // Set up a ResizeObserver to watch for height changes
    const resizeObserver = new ResizeObserver(updateInputHeight);
    if (inputContainerRef.current) {
      resizeObserver.observe(inputContainerRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Scroll to the last user message when a new one is added
  useEffect(() => {
    if (userMessageIds.length <= 1) return;

    // Full-page chat: composer is `position: fixed`; expander min-height hack helps scroll the last user
    // message into view. Embedded rail: composer is in normal flow — skip min-height (wrong scroll parent)
    // and avoid extra messages padding (see `messagesPaddingBottomPx`).
    if (!embedded && messagesContainerRef.current && lastUserMessageRef.current && chatContainerRef.current) {
      const lastUserMessageOffsetTop = lastUserMessageRef.current.offsetTop;
      if (chatContainerRef.current.scrollHeight < lastUserMessageOffsetTop + chatContainerRef.current.clientHeight) {
        const secondLastUserMessageOffsetTop = secondLastUserMessageRef.current ? secondLastUserMessageRef.current.offsetTop : 0;
        const maxExtraSpaceNeeded = chatContainerRef.current.clientHeight - 225;
        const spaceBetweenLastAndSecondLastUserMessage = lastUserMessageOffsetTop - secondLastUserMessageOffsetTop;
        const spaceNeeded = Math.min(maxExtraSpaceNeeded, spaceBetweenLastAndSecondLastUserMessage);
        minMessagesContainerHeight.current = chatContainerRef.current.scrollHeight + spaceNeeded;
      }
    }
    setReadyForAutoScroll(true);
  }, [embedded, userMessageIds]);

  // Auto scroll to the last user message when ready
  useEffect(() => {
    if (readyForAutoScroll) {
      if (lastUserMessageRef.current) {
        lastUserMessageRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
      setReadyForAutoScroll(false);
    }
  }, [readyForAutoScroll]);
  const getUserMessageRefAssignment = index => {
    // Get only user messages to determine which ref to assign
    const userMessages = messages.filter(isUserMessage);
    const userMessageIndex = userMessages.findIndex(msg => msg.id === messages[index].id);
    if (userMessageIndex === userMessages.length - 1) {
      return lastUserMessageRef;
    }
    if (userMessageIndex === userMessages.length - 2) {
      return secondLastUserMessageRef;
    }
    return null;
  };
  const messagesPaddingBottomPx = embedded ? 16 : inputHeight + 48;
  const expanderMinHeightStyle = embedded ? undefined : {
    minHeight: `${minMessagesContainerHeight.current}px`
  };
  return <div className={styles.chatContainer} ref={chatContainerRef}>
      <div className={styles.expander} style={expanderMinHeightStyle}>
        <div className={classNames(styles.chatContentContainer, styles.messagesContainer)} ref={messagesContainerRef} style={{
        paddingBottom: `${messagesPaddingBottomPx}px`
      }}>
          {messages.map((message, index) => isUserMessage(message) ? <div key={message.id} className={styles.userMessageContainer} ref={getUserMessageRefAssignment(index)}>
                <div className={styles.userMessage}>
                  <UserMessageContent>{message.content}</UserMessageContent>
                </div>
              </div> : <AiResponseMessage key={message.id} content={message.content} status={message.status} queryId={message.queryId} responseId={message.responseId} analyticsHandlers={analyticsHandlers} focusComposer={focusComposer} />)}
          {isLoading && <ThinkingLoader toolCalls={toolCalls} />}
        </div>
      </div>
      <div className={classNames(styles.chatContentContainer, styles.inputContainer)} ref={inputContainerRef}>
        <GrowableSearchSection ref={growableSearchRef} searchQuery={currentMessage} onSearchChange={setCurrentMessage} onSearch={handleSearch} location={COMPOSER_LOCATION.CHAT_PAGE} showSuggestions onSuggestionClick={handleSuggestionClick} responseBeingGenerated={isLoading} onCancelGeneration={onCancelGeneration} analyticsHandlers={analyticsHandlers} onNewChatPressed={onNewChatPressed} embedded={embedded} />
        <div className={styles.disclaimer}>
          <BodyText color="muted" size="small">{I18n.t('publisher.agent_hub.disclaimer')}</BodyText>
        </div>
        <div className={styles.inputContainerSpacer} />
      </div>
    </div>;
};

require("./ChatPage.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./ChatPage.js");